'use client'

import * as Sentry from '@sentry/nextjs'
import { useEffect } from 'react'
import {
  SKEW_DESCRIPTION,
  SKEW_TITLE,
  useSkewAwareError,
} from '@/lib/observability/use-skew-aware-error'

/**
 * Replaces the root layout when active, so it renders its own `<html>`/`<body>` and cannot lean on
 * app providers, the design system or global CSS — hence inline styles. Deploy skew (RANKSURF-8G)
 * is handled the same way as in every other boundary: a reload prompt, reported by the shared hook.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const { kind, isSkew, onReload } = useSkewAwareError(error, 'global-error')

  useEffect(() => {
    if (kind === 'ordinary') Sentry.captureException(error)
  }, [error, kind])

  return (
    <html lang="en">
      <body>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <h2>{isSkew ? SKEW_TITLE : 'Something went wrong!'}</h2>
          {isSkew ? <p>{SKEW_DESCRIPTION}</p> : null}
          <button
            onClick={() => (isSkew ? onReload() : reset())}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              cursor: 'pointer',
            }}
          >
            {isSkew ? 'Reload' : 'Try again'}
          </button>
        </div>
      </body>
    </html>
  )
}
